'use client'

/**
 * 「見た」を知らせる — 操作履歴の集計（2026-09-22 古西さん「ページを押したら1回と数える」・右腕 計画.md「09/22 古西さんの決め」）。
 *
 * 画面には何も出さない（return null・JSX を持たないので .ts）。一番外側の枠（app/layout.tsx）に1つだけ置き、
 * 住所（pathname）が変わるたびに /api/usage/view へ1回 POST する。
 * 画面の中でタブを切り替えるだけ（住所が変わらない）なら数えない（私の判断・計画.md の不明点「まずは住所で」）。
 *
 * 知らせないもの:
 *   - ログインしていない時（知らせても 401 になり、access_logs に失敗として残るだけ）
 *   - なりすまし中（サーバーの本人が相手の人になっている）
 *   - お客様向け・ログイン画面（lib/usage/countable-path.ts・サーバーも同じ判定で止める）
 * 失敗しても画面には何も出さない（数えるための知らせで、仕事を止める理由にならない）。
 */
import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { useAuthStore } from '@/lib/stores/auth'
import { isCountablePagePath } from '@/lib/usage/countable-path'

export const USAGE_VIEW_ENDPOINT = '/api/usage/view'

/** 受け口（engagement/view）が通す役割か。役割が無い・未割当（'user'）なら送らない */
export function shouldReportForRole(role: string | null | undefined): boolean {
  return typeof role === 'string' && role.length > 0 && role !== 'user'
}

export function UsageViewReporter(): null {
  const pathname = usePathname()
  const userId = useAuthStore((s) => s.user?.id ?? null)
  const role = useAuthStore((s) => s.user?.role ?? null)
  const isImpersonating = useAuthStore((s) => s.isImpersonating)
  const lastSent = useRef<string | null>(null)

  useEffect(() => {
    if (!userId || isImpersonating) return
    // 役割が未割当（'user'）の人は受け口が 403 で断る。送ると画面を移るたびに Sentry・監査ログ・
    // access_logs へ1件ずつ拒否が積もるので、送らない（門番 2026-09-22・evaluator の no_role_for_listing）
    if (!shouldReportForRole(role)) return
    if (!isCountablePagePath(pathname)) return
    // 同じ人が同じ住所にいる間は1回だけ（再描画で二重に数えない）
    const key = `${userId}:${pathname}`
    if (lastSent.current === key) return
    lastSent.current = key
    void fetch(USAGE_VIEW_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path: pathname }),
      // 画面を離れる瞬間でも届くように
      keepalive: true,
    }).catch(() => {
      // 届かなかった時は「送った」印を外す＝次にこの住所へ来た時にもう一度送る。
      // 画面には何も出さない（数えるための知らせなので、仕事を止める理由にならない）。
      // 🔴 黙って捨てない。印を戻すのが、この知らせにとっての意味のある代わりの一手
      //    （通信が一時的に切れただけの人が、その日ずっと数えられなくなるのを防ぐ）。
      if (lastSent.current === key) lastSent.current = null
    })
  }, [pathname, userId, role, isImpersonating])

  return null
}
